import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Loading, Modal } from '@carbon/react';

import { hideForm } from 'services/forms';
import { showToast } from 'services/toasts';
import { useDeleteUserMutation } from 'services/user';

/**
 * Confirmation modal that actually deletes the given user.
 *
 * @param {object} props
 * @param {{id: (string|number), email?: string}} props.user - target user
 * @param {() => void} props.hideModal - closes the modal
 */
const RemoveUserModal = ({ user, hideModal }) => {
  const dispatch = useDispatch();
  const [deleteUser] = useDeleteUserMutation();
  const [isLoading, setIsLoading] = useState(false);

  // Resolve the id once. Callers used to open this modal with a bare
  // `{ID: ...}` payload, which left the modal without a user to act on —
  // bail out loudly instead of silently rendering a no-op dialog.
  const userID = user?.id;
  const userLabel = user?.email || `#${userID || '?'}`;

  useEffect(() => {
    if (userID == null) {
      // eslint-disable-next-line no-console
      console.warn('RemoveUserModal opened without a valid user prop', user);
      dispatch(
        showToast({
          kind: 'error',
          title: 'Fehler',
          caption: 'Benutzer konnte nicht ermittelt werden',
        })
      );
      hideModal();
    }
  }, [userID, user, dispatch, hideModal]);

  if (userID == null) {
    return null;
  }

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const result = await deleteUser(userID).unwrap();
      // The backend follows the {status, message} convention even on soft
      // failures (HTTP 200 + status:false). unwrap() only throws for real
      // HTTP errors, so we still have to inspect the body.
      if (result && result.status === false) {
        throw new Error(result.message || 'Der Benutzer konnte nicht gelöscht werden');
      }
      dispatch(hideForm());
      dispatch(
        showToast({
          kind: 'success',
          title: 'Benutzer gelöscht',
          caption: `${userLabel} wurde erfolgreich gelöscht`,
        })
      );
    } catch (err) {
      // Pull the most specific message we can: backend JSON {message},
      // backend plain-text body, then our locally thrown fallback.
      const backendMessage =
        err?.data?.message ||
        (typeof err?.data === 'string' && err.data) ||
        err?.message;
      dispatch(
        showToast({
          kind: 'error',
          title: 'Fehler',
          caption: backendMessage || 'Der Benutzer konnte nicht gelöscht werden',
        })
      );
    } finally {
      setIsLoading(false);
      hideModal();
    }
  };

  return (
    <Modal
      open={true}
      danger={true}
      size="sm"
      primaryButtonText="Löschen"
      secondaryButtonText="Abbrechen"
      primaryButtonDisabled={isLoading}
      modalHeading={`Soll der Benutzer ${userLabel} wirklich gelöscht werden?`}
      onRequestClose={hideModal}
      onRequestSubmit={handleSubmit}
    >
      <Loading active={isLoading} withOverlay={true} />
    </Modal>
  );
};

export default RemoveUserModal;
